/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Carousel from "@monsma/Overrides/Components/Carousel";
import React from "react";
import { css } from "styled-components";

const enum fields {
    slides = "slides",
    autoPlay = "autoPlay",
}

interface SlideModel {
    fields: {
        copy: string;
        primaryLabel: string;
        secondaryLabel: string;
    };
}

interface IndustryRecognitionProps extends WidgetProps {
    fields: {
        [fields.slides]: SlideModel[];
        [fields.autoPlay]: string;
    };
}

interface IndustryRecognitionStyles {
    caruselWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    copy?: TypographyPresentationProps;
    labelsWrapper?: InjectableCss;
    primaryLabel?: TypographyPresentationProps;
    secondaryLabel?: TypographyPresentationProps;
}

const styles: IndustryRecognitionStyles = {
    caruselWrapper: {
        css: css`
            .swiper-slide {
                margin-bottom: 5px;
            }

            .swiper-navigation {
                margin-top: 35px;
            }
        `,
    },
    wrapper: {
        css: css`
            border: 1px solid ${getColor("common.ui12")};
            border-radius: 6px;
            background-color: ${getColor("common.background")};
            padding: 20px;
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        `,
    },
    copy: {
        lineHeight: "26px",
    },
    labelsWrapper: {
        css: css`
            margin-top: 30px;
            display: flex;
            flex-direction: column;
        `,
    },
    primaryLabel: {
        weight: "bold",
        css: css`
            margin-bottom: 7px;
        `,
    },
    secondaryLabel: {
        size: 13,
        lineHeight: "22px",
    },
};

const INDUSTRY_RECOGNITION_CAROUSEL_ID = "industry-recognition-carousel-id";

const IndustryRecognition: React.FC<IndustryRecognitionProps> = ({ fields }) => {
    const slides = mapReviewsToSlides(fields.slides);
    return (
        <StyledWrapper {...styles.caruselWrapper}>
            <Carousel
                id={INDUSTRY_RECOGNITION_CAROUSEL_ID}
                maxNumberOfColumns={2}
                {...({ showArrows: true, showDots: true, slidesGap: 15,  } as any)}
                slides={slides}
                autoPlay={fields.autoPlay}
            />
        </StyledWrapper>
    );
};

const mapReviewsToSlides = (slides: any) => {
    return slides?.map((slide: any, index: any) => {
        const { copy, primaryLabel, secondaryLabel } = slide.fields;
        return {
            id: `${INDUSTRY_RECOGNITION_CAROUSEL_ID}-${index}`,
            renderComponent: function ReviewsCarouselRenderer() {
                return (
                    <StyledWrapper {...styles.wrapper}>
                        <Typography {...styles.copy}>{copy}</Typography>
                        {(primaryLabel || secondaryLabel) && (
                            <StyledWrapper {...styles.labelsWrapper}>
                                {primaryLabel && <Typography {...styles.primaryLabel}>{primaryLabel}</Typography>}
                                {secondaryLabel && <Typography {...styles.secondaryLabel}>{secondaryLabel}</Typography>}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                );
            },
        };
    });
};

const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.slides,
            displayName: "Customer Review Slides",
            editorTemplate: "ListField",
            getDisplay: () => {
                return "Customer Review Slide";
            },
            defaultValue: [],
            fieldType: "General",
            fieldDefinitions: [
                {
                    name: "copy",
                    displayName: "Customer Review Copy",
                    editorTemplate: "TextField",
                    defaultValue: "",
                    isRequired: true,
                    maxLength: 1000,
                },
                {
                    name: "primaryLabel",
                    displayName: "Primary Label (First Name and Last Name)",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "secondaryLabel",
                    displayName: "Secondary Label (Business)",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
            ],
        },
        {
            name: fields.autoPlay,
            displayName: "Auto Play in Seconds",
            editorTemplate: "IntegerField",
            defaultValue: 0,
            placeholder: "Enter a Number of seconds to make auto play works! e.g: 1",
            fieldType: "General",
            min: 0,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: IndustryRecognition,
    definition,
};

export default widgetModule;
